// src/pages/Home.js (or wherever your Home component is)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
// import Sidebar from '../components/Sidebar'; // REMOVE THIS
import Footerbar from '../components/Footerbar'; // ADD THIS
import Navbar from '../components/Navbar'; // Keep or restyle Navbar as needed
import CustomAlert from '../components/CustomAlert'; // ADD
import ConfirmationModal from '../components/ConfirmationModal'; // ADD
import { Book, Copy, Gift, Mic2, PlusCircle, Radio, Users } from 'lucide-react';
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
// Firebase imports remain the same
import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { initDynamicFirebase } from '../constants/initDynamicFirebase';
import removeBar from '../constants/removeBar';
import { forgetShowSession, savedShowSession } from '../lib/showSession';
import { freshShowLocation, isShowLocationError } from '../lib/showLocation';
import { clearActiveRoomId } from '../lib/activeRoomSession';

// --- Helper Function (remains the same) ---
function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

const API_URL = 'https://apem.riderup.uk/api';

const getRoomCoordinates = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
        const error = new Error('Este dispositivo não oferece acesso à localização.');
        error.code = 'GEOLOCATION';
        reject(error);
        return;
    }

    navigator.geolocation.getCurrentPosition(
        ({ coords }) => resolve({ latitude: coords.latitude, longitude: coords.longitude }),
        (positionError) => {
            const error = new Error(positionError.code === 1
                ? 'Permita o acesso à localização para continuar conectado à sala.'
                : 'Não foi possível verificar sua localização. Confira o GPS.');
            error.code = 'GEOLOCATION';
            reject(error);
        },
        { enableHighAccuracy: true, timeout: 12000, maximumAge: 5000 }
    );
});

const roomTrackThumbnail = (track) => {
    if (typeof track?.thumbnail === 'string') return track.thumbnail;
    if (track?.thumbnail?.url) return track.thumbnail.url;
    const videoId = track?.video_id || track?.videoId || track?.youtube_id || track?.youtube_video_id;
    return videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : '';
};

// --- Component ---
export default function Home() {
    // const [isSidebarOpen, setIsSidebarOpen] = useState(false); // REMOVE THIS
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isLoadingPayment, setIsLoadingPayment] = useState(false);
    const [musics, setMusics] = useState([]);
    const [isCreditModalOpen, setIsCreditModalOpen] = useState(false);
    const [paymentData, setPaymentData] = useState(null);
    const [unsubscribeMusic, setUnsubscribeMusic] = useState(null);
    const [alertInfo, setAlertInfo] = useState({ show: false, message: "", type: "error" });
    const [confirmBarLogout, setConfirmBarLogout] = useState({ show: false, message: "" });
    const [activeRoom, setActiveRoom] = useState(null);
    const [hasActiveRoomSession, setHasActiveRoomSession] = useState(() => Boolean(localStorage.getItem('activeRoomId')));
    const [roomConnectionNotice, setRoomConnectionNotice] = useState('');
    const [activeShow, setActiveShow] = useState(null);
    const [hasActiveShowSession, setHasActiveShowSession] = useState(false);
    const [showSessionChecked, setShowSessionChecked] = useState(false);
    const [showConnectionNotice, setShowConnectionNotice] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            setShowSessionChecked(true);
            return undefined;
        }

        let mounted = true;
        let checking = false;
        let currentShowId = null;
        const refreshShow = async () => {
            if (checking || document.hidden) return;
            checking = true;
            try {
                currentShowId = await savedShowSession(token);
                if (!currentShowId) {
                    if (mounted) {
                        setActiveShow(null);
                        setHasActiveShowSession(false);
                        setShowSessionChecked(true);
                    }
                    return;
                }
                if (mounted) {
                    setHasActiveShowSession(true);
                    setShowSessionChecked(true);
                }

                const location = await freshShowLocation();
                if (!mounted) return;
                const headers = { Authorization: `Bearer ${token}` };
                await axios.post(`${API_URL}/artist-shows/${currentShowId}/presence`, location, { headers });
                const response = await axios.get(`${API_URL}/artist-shows/${currentShowId}`, { params: location, headers });
                if (mounted) {
                    setActiveShow(response.data.show);
                    setShowConnectionNotice('');
                }
            } catch (error) {
                if (!mounted) return;
                if (isShowLocationError(error) || [404, 410].includes(error.response?.status)) {
                    forgetShowSession(currentShowId || undefined);
                    setActiveShow(null);
                    setHasActiveShowSession(false);
                    setShowSessionChecked(true);
                    setShowConnectionNotice(error.response?.data?.message || error.message || 'A conexão com o show foi encerrada.');
                } else if (error.response?.status === 401) {
                    forgetShowSession(currentShowId || undefined);
                    setActiveShow(null);
                    setHasActiveShowSession(false);
                    setShowSessionChecked(true);
                    setShowConnectionNotice('Sua sessão expirou. Entre novamente.');
                } else {
                    console.error('Erro ao atualizar o show na Home:', error);
                    setShowSessionChecked(true);
                    setShowConnectionNotice('Não foi possível atualizar o show agora. Tentando novamente.');
                }
            } finally {
                checking = false;
            }
        };

        const timer = window.setInterval(refreshShow, 10000);
        const resume = () => { if (!document.hidden) void refreshShow(); };
        void refreshShow();
        document.addEventListener('visibilitychange', resume);
        window.addEventListener('focus', resume);
        return () => {
            mounted = false;
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', resume);
            window.removeEventListener('focus', resume);
        };
    }, []);

    useEffect(() => {
        const roomId = localStorage.getItem('activeRoomId');
        const token = localStorage.getItem('token');
        if (!roomId || !token) {
            setActiveRoom(null);
            setHasActiveRoomSession(false);
            return undefined;
        }
        setHasActiveRoomSession(true);

        let mounted = true;
        let checking = false;
        const refreshRoom = async () => {
            if (checking) return;
            checking = true;
            try {
                const coordinates = await getRoomCoordinates();
                const headers = { Authorization: `Bearer ${token}` };
                await axios.post(`${API_URL}/rooms/${roomId}/heartbeat`, coordinates, { headers });
                const response = await axios.get(`${API_URL}/rooms/${roomId}`, { headers });
                if (mounted) {
                    setActiveRoom(response.data.room);
                    setRoomConnectionNotice('');
                }
            } catch (error) {
                if (!mounted) return;
                if ([403, 404, 409, 410].includes(error.response?.status) || error.code === 'GEOLOCATION') {
                    clearActiveRoomId();
                    setActiveRoom(null);
                    setHasActiveRoomSession(false);
                    setRoomConnectionNotice(error.response?.data?.message || error.message || 'Sua conexão com a sala foi encerrada.');
                } else {
                    console.error('Erro ao atualizar a sala na Home:', error);
                    setRoomConnectionNotice('Não foi possível atualizar os dados da sala agora. Tente novamente em instantes.');
                }
            } finally {
                checking = false;
            }
        };

        void refreshRoom();
        const timer = window.setInterval(refreshRoom, 15000);
        return () => {
            mounted = false;
            window.clearInterval(timer);
        };
    }, []);

    // --- useEffects (fetchUserData, connectToFirebase, solicitarPermissaoPush) remain largely the same ---
    // Make sure API calls and Firebase logic are correct
    useEffect(() => {
        const fetchUserData = async () => {
            setLoading(true);
            // await solicitarPermissaoPush(); // Consider when/if to call this
            try {
                const token = localStorage.getItem('token');
                if (!token) { // Handle case where user is not logged in
                    navigate('/'); // Redirect to login
                    return;
                }
                const response = await axios.get('https://apem.riderup.uk/api/user', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUserData(response.data);

                const savedBar = response.data.client;
                if (savedBar) {
                    await connectToFirebase(savedBar);
                } else {
                    // If no bar is connected, clear music listener if it exists
                    if (unsubscribeMusic) unsubscribeMusic();
                    setMusics([]);
                }
                // Call push permission request after user data is potentially loaded
               // await solicitarPermissaoPush();

            } catch (error) {
                console.error('Erro ao buscar usuário:', error);
                if (error.response && error.response.status === 401) {
                    // Unauthorized, token might be invalid
                    localStorage.removeItem('token');
                    navigate('/');
                }
                // Maybe set an error state here instead of just logging
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();

        // Cleanup function for the listener when the component unmounts
        return () => {
            if (unsubscribeMusic) {
                unsubscribeMusic();
                console.log('Firebase listener unsubscribed.');
            }
        };
    }, []); // Empty dependency array means this runs once on mount


    const connectToFirebase = async (bar) => {
        // Cancel previous listener *before* setting up a new one
        if (unsubscribeMusic) {
            unsubscribeMusic();
            console.log('Previous Firebase listener unsubscribed.');
            setUnsubscribeMusic(null); // Reset state
        }

        // Ensure bar details are present
        if (!bar || !bar.firebase_api_key) {
            console.error("Bar Firebase configuration is missing.");
            setMusics([]); // Clear music if config is bad
            return;
        }
        const config = {
            apiKey: "AIzaSyBMVTvCciOYjyTFES0wapwkiVxTRL_9WJU",
            authDomain: "apemall.firebaseapp.com",
            projectId: "apemall",
            storageBucket: "apemall.firebasestorage.app",
            messagingSenderId: "824620532506",
            appId: "1:824620532506:web:1378444a5c52e09807e2a3",
            measurementId: "G-1YDCLNVCKH"
        };
        try {
            const db = await initDynamicFirebase(config);
            const empresaId = String(bar.id);
            const videosRef = collection(db, 'empresa', empresaId, 'lista');
            const q = query(videosRef, orderBy('fecha', 'asc'));
            console.log(q);
            const unsubscribe = onSnapshot(q, (querySnapshot) => {
                const musicData = querySnapshot.docs.map(doc => ({
                    id: doc.data().id, // Keep original ID if needed
                    docId: doc.id, // Firebase document ID for potential actions
                    title: doc.data().title || 'Sem título',
                    author: doc.data().author || 'Desconhecido',
                    thumbnail: `https://i.ytimg.com/vi/${doc.data().id}/hqdefault.jpg`
                }));
                console.log('Músicas atualizadas:', musicData);
                setMusics(musicData);
            }, (error) => {
                // Handle errors during listening
                console.error("Error listening to music updates:", error);
                setMusics([]); // Clear music on error
                if (unsubscribeMusic) {
                    unsubscribeMusic(); // Attempt cleanup on error too
                    setUnsubscribeMusic(null);
                }
            });

            // Store the new unsubscribe function
            setUnsubscribeMusic(() => unsubscribe);
            console.log('Firebase listener subscribed for bar:', bar.name);

        } catch (error) {
            console.error("Error initializing Firebase for bar:", bar.name, error);
            setMusics([]); // Clear music list on initialization error
            if (unsubscribeMusic) {
                unsubscribeMusic(); // Cleanup
                setUnsubscribeMusic(null);
            }
        }
    };

    async function solicitarPermissaoPush() {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            console.warn('Push notifications or Service Worker not supported.');
            return;
        }
        try {
            const permission = await Notification.requestPermission();
            if (permission !== 'granted') {
                console.log('Permissão negada para notificações.');
                return;
            }

            // Ensure service worker is ready
            const registration = await navigator.serviceWorker.ready;

            // Get subscription
            let subscription = await registration.pushManager.getSubscription();

            // If no subscription exists, create one
            if (!subscription) {
                const vapidPublicKey = 'BMdwEh-BOLWrYRDz9TtAhK1qqFcCbSxRjUupXS_fZHHIIRLlMOiSleL56NzebRqNcPWP3eOEva2yADfE2EBVXRQ'; // Your VAPID Public Key
                subscription = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
                });
                console.log('New Push Subscription obtained:', subscription);
            } else {
                console.log('Existing Push Subscription found:', subscription);
            }


            const token = localStorage.getItem('token');
            if (token && subscription) {
                // Send subscription to backend
                await axios.post('https://apem.riderup.uk/api/notification', subscription.toJSON(), { // Send as JSON
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });
                console.log('Push subscription sent to backend.');
            } else {
                console.error('Token ou subscription não encontrados para enviar ao backend.');
            }
        } catch (error) {
            console.error('Erro ao solicitar permissão/subscription de push:', error);
        }
    }


    // --- Handlers (handleBarLogout, handlePayment, copyPixCode) remain largely the same ---
    const handleBarLogout = async () => {
        if (!userData || !userData.client) return;
        setConfirmBarLogout({ show: true, message: `Tem certeza que deseja sair do bar ${userData.client.name}?` });
    };

    const executeBarLogout = async () => {
        setConfirmBarLogout({ show: false, message: "" });
        try {
            await removeBar(userData.client.id); // Assuming removeBar handles API call
            // Update user data locally *without* fetching again
            setUserData(prevData => ({ ...prevData, client: null }));
            setMusics([]); // Clear music list
            if (unsubscribeMusic) {
                unsubscribeMusic(); // Unsubscribe from Firebase listener
                setUnsubscribeMusic(null);
            }
            setAlertInfo({ show: true, message: "Desconectado com sucesso.", type: 'success' });
        } catch (error) {
            console.error('Erro ao sair do bar:', error);
            setAlertInfo({ show: true, message: "Erro ao sair do bar. Verifique sua conexão ou tente novamente.", type: 'error' });
        }
    };

    const handlePayment = async (amount) => {
        setIsLoadingPayment(true);
        setPaymentData(null); // Clear previous payment data
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `https://apem.riderup.uk/api/payment/${amount}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            // Ensure the structure is as expected before setting
            if (response.data?.point_of_interaction?.transaction_data) {
                setPaymentData(response.data);
            } else {
                console.error('Estrutura inesperada recebida da API de pagamento:', response.data);
                setAlertInfo({ show: true, message: "Erro ao processar dados do pagamento. Tente novamente.", type: 'error' });
            }
        } catch (error) {
            console.error('Erro ao gerar pagamento:', error);
            setAlertInfo({ show: true, message: `Erro ao gerar o pagamento: ${error.response?.data?.message || error.message || 'Erro desconhecido'}`, type: 'error' });
        } finally {
            setIsLoadingPayment(false);
        }
    };

    const copyPixCode = () => {
        const pixCode = paymentData?.point_of_interaction?.transaction_data?.qr_code;
        if (pixCode) {
            navigator.clipboard.writeText(pixCode)
                .then(() => {
                    setAlertInfo({ show: true, message: "Código PIX copiado!", type: 'success' });
                })
                .catch(err => {
                    console.error('Erro ao copiar código PIX:', err);
                    setAlertInfo({ show: true, message: "Não foi possível copiar o código PIX.", type: 'error' });
                    // Fallback: Prompt the user to copy manually
                    window.prompt("Copie o código PIX manualmente:", pixCode);
                });
        } else {
            setAlertInfo({ show: true, message: "Nenhum código PIX para copiar.", type: 'error' });
        }
    };


    const isConnectedToBar = userData && userData.client;

    const cancelBarLogout = () => {
        setConfirmBarLogout({ show: false, message: "" });
    };

    const handleCloseAlert = () => setAlertInfo({ ...alertInfo, show: false });

    // --- Render Logic ---
    return (
        <div style={styles.pageContainer}> {/* Use a container for background */}
            {alertInfo.show && <CustomAlert message={alertInfo.message} type={alertInfo.type} onclose={handleCloseAlert} />}
            <ConfirmationModal show={confirmBarLogout.show} message={confirmBarLogout.message} onConfirm={executeBarLogout} onCancel={cancelBarLogout} />
            {/* Pass null or remove openSidebar prop if Navbar doesn't need it */}
            <Navbar />

            {/* Main Content Area */}
            <div style={styles.contentArea}>
                {loading ? (
                    <p style={styles.loadingText}>Carregando...</p>
                ) : userData ? (
                    <div style={styles.userInfoContainer}>
                        <img
                            src={userData.avatar ? `https://apem.riderup.uk${userData.avatar}` : 'https://avatars.githubusercontent.com/u/89489383?v=4'} // Added fallback URL parameter
                            alt="Avatar"
                            style={styles.avatar}
                        />
                        <h2 style={styles.greeting}>Olá, {userData.name}</h2>
                        <div style={styles.creditInfo}>
                            <span style={styles.creditText}>{userData.credit ?? 0} Créditos</span>
                            <PlusCircle
                                size={26}
                                color="#a0a0ff" // Lighter purple for icon
                                onClick={() => setIsCreditModalOpen(true)}
                                style={styles.addCreditIcon}
                            />
                        </div>

                        {/* Conditional Buttons */}
                        {isConnectedToBar && (
                            <div style={styles.actionButtonsContainer}>
                                {/* Aniversário Button - Hidden by default in original code */}
                                <button
                                    onClick={() => navigate('/aniversarios')}
                                    style={{ ...styles.actionButton, ...styles.birthdayButton, display: 'none' }} // Keep hidden unless logic changes
                                >
                                    <Gift size={20} /> Mandar Mensagem
                                </button>
                                {/* Cardápio Button */}
                                <button
                                    onClick={() => window.open(userData.client.client_url ?? '#', '_blank')} // Added fallback '#'
                                    style={{ ...styles.actionButton, ...styles.menuButton, display: 'none' }}
                                    disabled={!userData.client.client_url} // Disable if no URL
                                >
                                    <Book size={20} /> Ver Cardápio
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    <p style={styles.errorText}>Erro ao carregar dados do usuário.</p>
                )}

                {isConnectedToBar && userData ? (
                    <div style={styles.barSection}>
                        <div style={styles.connectedBarInfo}>
                            <h3 style={styles.barName}>Conectado a: {userData.client.name}</h3>
                            {userData.client.logo && <img src={userData.client.logo} alt={`${userData.client.name} Logo`} style={styles.barLogo} />}
                            <p style={styles.barAddress}>{userData.client.address}</p>
                            <button onClick={handleBarLogout} style={styles.logoutBarButton}>Sair do Bar</button>
                        </div>
                        <div style={styles.musicInfoContainer}>
                            {musics.length > 0 ? (
                                <>
                                    {musics[0] && <div style={styles.musicItem}>
                                        <h3 style={styles.musicHeader}>Tocando agora</h3>
                                        <div style={styles.musicDetails}>
                                            <img src={musics[0].thumbnail} alt="" style={styles.thumbnail} />
                                            <div style={styles.musicText}><p style={styles.musicTitle}>{musics[0].title}</p><p style={styles.musicAuthor}>Por: {musics[0].author}</p></div>
                                        </div>
                                    </div>}
                                    {musics[1] && <div style={styles.musicItem}>
                                        <h3 style={styles.musicHeader}>Próxima</h3>
                                        <div style={styles.musicDetails}>
                                            <img src={musics[1].thumbnail} alt="" style={styles.thumbnail} />
                                            <div style={styles.musicText}><p style={styles.musicTitle}>{musics[1].title}</p><p style={styles.musicAuthor}>Por: {musics[1].author}</p></div>
                                        </div>
                                    </div>}
                                    {musics.length > 1 && <button onClick={() => navigate('/list')} style={styles.fullListButton}>Ver Lista Completa</button>}
                                </>
                            ) : <p style={styles.noMusicText}>Nenhuma música na fila no momento.</p>}
                            {musics.length === 1 && <p style={styles.noMusicText}>Aguardando próximas músicas...</p>}
                        </div>
                    </div>
                ) : !loading && showSessionChecked && !hasActiveShowSession && !hasActiveRoomSession && <button onClick={() => navigate('/bars')} style={styles.selectBarButton}>Selecionar Estabelecimento</button>}

                {showConnectionNotice && <p style={styles.roomNotice} role="status">{showConnectionNotice}</p>}
                {activeShow && (
                    <section style={styles.contextPanel} aria-label="Show conectado">
                        <div style={styles.showHomeHeading}>
                            {activeShow.artist_profile?.photo_url ? <img src={activeShow.artist_profile.photo_url} alt="" style={styles.showHomePhoto} /> : <span style={styles.contextIcon}><Mic2 size={22} /></span>}
                            <div style={styles.musicText}>
                                <p style={styles.showLiveLabel}><Radio size={14} />Show ao vivo</p>
                                <h3 style={styles.contextTitle}>{activeShow.artist_profile?.stage_name || activeShow.artist_profile?.user?.name || 'Cantor'}</h3>
                                <p style={styles.contextSubtitle}>{activeShow.title}</p>
                                <p style={styles.contextSubtitle}>{activeShow.location_name || activeShow.venue_name || activeShow.address || 'Localização do show'}</p>
                            </div>
                            <span style={styles.showParticipantCount}><Users size={15} />{activeShow.participant_count || 0}</span>
                        </div>
                    </section>
                )}

                {roomConnectionNotice && <p style={styles.roomNotice} role="status">{roomConnectionNotice}</p>}
                {activeRoom && (
                    <section style={styles.contextPanel} aria-label="Playlist da sala">
                        <div style={styles.roomHeading}>
                            <div style={styles.contextHeading}><span style={styles.contextIcon}><Radio size={22} /></span><div><h3 style={styles.contextTitle}>{activeRoom.name}</h3><p style={styles.contextSubtitle}>{activeRoom.members?.length || 0} conectados</p></div></div>
                        </div>
                        <div style={styles.musicInfoContainer}>
                            {activeRoom.tracks?.length ? (
                                <>
                                    {[activeRoom.tracks[0], activeRoom.tracks[1]].filter(Boolean).map((track, index) => (
                                        <div style={styles.musicItem} key={track.id || track.video_id || index}>
                                            <h3 style={styles.musicHeader}>{index === 0 ? 'Tocando agora' : 'Próxima'}</h3>
                                            <div style={styles.musicDetails}>
                                                {roomTrackThumbnail(track) && <img src={roomTrackThumbnail(track)} alt="" style={styles.thumbnail} />}
                                                <div style={styles.musicText}><p style={styles.musicTitle}>{track.title || 'Música sem título'}</p><p style={styles.musicAuthor}>Por: {track.requested_by || track.requester_name || track.artist || 'Participante'}</p></div>
                                            </div>
                                        </div>
                                    ))}
                                    <p style={styles.roomQueueCount}><Users size={15} />{activeRoom.tracks.length} {activeRoom.tracks.length === 1 ? 'música na fila' : 'músicas na fila'}</p>
                                </>
                            ) : <p style={styles.noMusicText}>Nenhuma música na fila da sala.</p>}
                        </div>
                    </section>
                )}
            </div> {/* End Content Area */}

            {/* Credit Purchase Modal */}
            {isCreditModalOpen && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modalContent}>
                        <h2 style={styles.modalTitle}>Comprar Créditos</h2>

                        {/* Payment Buttons */}
                        <button onClick={() => handlePayment(10)} disabled={isLoadingPayment} style={styles.paymentOptionButton(isLoadingPayment)}>
                            {isLoadingPayment ? "Processando..." : "5 Créditos - R$ 10"}
                        </button>
                        <button onClick={() => handlePayment(20)} disabled={isLoadingPayment} style={styles.paymentOptionButton(isLoadingPayment)}>
                            {isLoadingPayment ? "Processando..." : "11 Créditos - R$ 20"}
                        </button>

                        {/* Payment Display Area */}
                        {paymentData && (
                            <div style={styles.paymentDetailsContainer}>
                                <p style={styles.paymentInstructions}>Escaneie o QR Code ou copie o código PIX:</p>
                                <img
                                    src={`data:image/png;base64,${paymentData.point_of_interaction.transaction_data.qr_code_base64}`}
                                    alt="QR Code PIX"
                                    style={styles.qrCodeImage}
                                />
                                <button onClick={copyPixCode} style={styles.copyPixButton}>
                                    <Copy size={18} /> Copiar Código PIX
                                </button>
                                <p style={styles.pixCodePreview}>
                                    {paymentData.point_of_interaction.transaction_data.qr_code.substring(0, 30)}...
                                </p>
                            </div>
                        )}
                        {isLoadingPayment && !paymentData && ( // Show specific loading message for payment generation
                            <p style={styles.loadingText}>Gerando informações de pagamento...</p>
                        )}


                        {/* Close Button */}
                        <button onClick={() => setIsCreditModalOpen(false)} style={styles.modalCloseButton}>
                            Fechar
                        </button>
                    </div>
                </div>
            )}

            <Footerbar /> {/* ADD THIS */}
        </div> // End Page Container
    );
}

// --- Styles Object ---
// --- Styles Object (COMPLETE) ---
const styles = {
    pageContainer: {
        backgroundColor: '#12121f', // Very dark blue/purple background
        color: '#f0f0f0', // Light text color
        minHeight: '100vh', // Ensure it covers the full height
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden', // Force hide horizontal overflow on the main container
        boxSizing: 'border-box', // Apply box-sizing globally within this container if needed
    },
    contentArea: {
        padding: '20px',
        paddingTop: '70px', // Space below Navbar (adjust if Navbar height changes: 60px)
        paddingBottom: '80px', // Space above Footerbar (adjust if Footerbar height changes: 65px)
        maxWidth: '700px',
        marginLeft: 'auto',
        marginRight: 'auto',
        textAlign: 'center',
        flexGrow: 1, // Allow content to take available space
        width: '100%', // Ensure padding works correctly on smaller screens
        boxSizing: 'border-box', // Ensure padding is included in width calculation
    },
    loadingText: {
        fontSize: '18px',
        color: '#a0a0ff',
        marginTop: '40px',
        textAlign: 'center', // Ensure centered
        width: '100%', // Ensure takes width
    },
    errorText: {
        fontSize: '16px',
        color: '#ff6060', // Reddish color for errors
        marginTop: '30px',
        textAlign: 'center',
        padding: '10px',
        backgroundColor: 'rgba(255, 96, 96, 0.1)', // Slight background for visibility
        borderRadius: '8px',
        border: '1px solid rgba(255, 96, 96, 0.3)',
        overflowWrap: 'break-word', // Allow error messages to wrap
        wordWrap: 'break-word',
    },
    userInfoContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: '30px',
        backgroundColor: '#1a1a2e', // Slightly lighter dark card background
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.4)',
        boxSizing: 'border-box', // Add box-sizing
    },
    avatar: {
        width: '90px',
        height: '90px',
        borderRadius: '50%',
        marginBottom: '15px',
        border: '3px solid #a0a0ff', // Purple border
        objectFit: 'cover', // Ensure avatar image covers the area well
    },
    greeting: {
        fontSize: '22px',
        fontWeight: 'bold',
        margin: '0 0 10px 0',
        color: '#ffffff',
        overflowWrap: 'break-word', // Allow long names to wrap
        wordWrap: 'break-word', // Older fallback
        hyphens: 'auto', // Optional: allow hyphenation
        textAlign: 'center', // Ensure centered text after wrapping
        maxWidth: '100%', // Ensure it doesn't exceed container width
    },
    creditInfo: {
        display: 'none',
        alignItems: 'center',
        gap: '12px',
        backgroundColor: '#2a2a4e', // Darker purple background for credits
        padding: '8px 15px',
        borderRadius: '20px',
        marginBottom: '15px', // Added margin below credits
    },
    creditText: {
        color: '#e0e0ff', // Lighter purple text
        fontSize: '18px',
        fontWeight: '500',
    },
    addCreditIcon: {
        cursor: 'pointer',
        transition: 'transform 0.2s ease',
        // ':hover': { transform: 'scale(1.1)' } // Requires CSS Modules/Styled Components
    },
    actionButtonsContainer: {
        display: 'flex',
        flexWrap: 'wrap', // Allow buttons to wrap on small screens if necessary
        gap: '15px',
        marginTop: '10px',
        width: '100%',
        justifyContent: 'center',
    },
    contextPanel: {
        backgroundColor: '#1a1a2e',
        padding: '20px',
        marginBottom: '16px',
        border: '1px solid #30304b',
        borderRadius: '12px',
        textAlign: 'left',
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
        boxSizing: 'border-box',
    },
    contextHeading: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        minWidth: 0,
        textAlign: 'left',
    },
    contextIcon: {
        display: 'grid',
        placeItems: 'center',
        width: '44px',
        height: '44px',
        flexShrink: 0,
        color: '#c4c4ff',
        backgroundColor: '#2a2a4e',
        borderRadius: '10px',
    },
    contextTitle: {
        margin: 0,
        color: '#ffffff',
        fontSize: '17px',
        overflowWrap: 'anywhere',
    },
    contextSubtitle: {
        margin: '4px 0 0',
        color: '#aaaabd',
        fontSize: '13px',
        lineHeight: 1.4,
    },
    showHomeHeading: {
        display: 'flex',
        alignItems: 'center',
        gap: '13px',
        minWidth: 0,
        textAlign: 'left',
    },
    showHomePhoto: {
        width: '58px',
        height: '58px',
        flexShrink: 0,
        objectFit: 'cover',
        borderRadius: '50%',
        border: '2px solid #65658d',
    },
    showLiveLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        margin: '0 0 5px',
        color: '#75e0b8',
        fontSize: '12px',
        fontWeight: 700,
    },
    showParticipantCount: {
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        marginLeft: 'auto',
        paddingLeft: '8px',
        color: '#c4c4d7',
        fontSize: '13px',
    },
    roomHeading: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        marginBottom: '20px',
        paddingBottom: '16px',
        borderBottom: '1px solid #3a3a5e',
    },
    roomQueueCount: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        margin: '8px 0 0',
        color: '#aaaabd',
        fontSize: '13px',
    },
    roomNotice: {
        margin: '0 0 14px',
        padding: '10px 12px',
        color: '#ffd6d6',
        backgroundColor: 'rgba(180, 60, 60, 0.18)',
        border: '1px solid rgba(255, 110, 110, 0.35)',
        borderRadius: '8px',
        fontSize: '13px',
        textAlign: 'left',
    },
    actionButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: 'white',
        padding: '10px 18px',
        borderRadius: '8px',
        border: 'none',
        fontSize: '16px',
        cursor: 'pointer',
        transition: 'background-color 0.3s ease, opacity 0.3s ease',
        fontWeight: '500',
        flexShrink: 0, // Prevent buttons from shrinking awkwardly if wrapping
        // Base styles - specific button styles below will override backgroundColor
    },
    birthdayButton: {
        backgroundColor: '#8A2BE2', // Purple
        // ':hover': { backgroundColor: '#7a1cc2' } // Requires CSS Modules/Styled Components
    },
    menuButton: {
        backgroundColor: '#50C878', // Emerald Green
        // ':hover': { backgroundColor: '#40b068' } // Requires CSS Modules/Styled Components
        // Disabled state needs to be applied conditionally in the JSX:
        // style={ !userData?.client?.client_url
        //    ? { ...styles.actionButton, ...styles.menuButton, ...styles.disabledButton }
        //    : { ...styles.actionButton, ...styles.menuButton }
        // }
    },
    // Style for disabled buttons (applied conditionally)
    disabledButton: {
        backgroundColor: '#555', // Darker gray for disabled state
        cursor: 'not-allowed',
        opacity: 0.6,
    },
    barSection: {
        backgroundColor: '#1a1a2e', // Card background
        padding: '25px',
        borderRadius: '12px',
        marginBottom: '30px',
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.4)',
        boxSizing: 'border-box', // Add box-sizing
    },
    connectedBarInfo: {
        marginBottom: '25px',
        paddingBottom: '20px',
        borderBottom: '1px solid #3a3a5e', // Separator line
    },
    barName: {
        fontSize: '20px',
        fontWeight: 'bold',
        color: '#c0c0ff', // Light purple variant
        marginBottom: '10px',
        overflowWrap: 'break-word', // Allow long names to wrap
        wordWrap: 'break-word', // Older fallback
    },
    barLogo: {
        maxWidth: '80%', // Responsive logo size
        maxHeight: '100px', // Limit height
        height: 'auto', // Maintain aspect ratio
        display: 'block',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginBottom: '10px',
        borderRadius: '8px', // Slightly rounded corners for the logo
    },
    barAddress: {
        fontSize: '14px',
        color: '#b0b0b0', // Lighter gray
        marginBottom: '15px',
        overflowWrap: 'break-word', // Allow long addresses to wrap
        wordWrap: 'break-word', // Older fallback
        textAlign: 'center', // Ensure address stays centered if it wraps
    },
    logoutBarButton: {
        marginTop: '10px',
        backgroundColor: '#E60000',
        color: 'white',
        padding: '10px 20px',
        borderRadius: '8px',
        border: 'none',
        fontSize: '15px',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        // ':hover': { backgroundColor: '#c00000' } // Requires CSS Modules/Styled Components
    },
    musicInfoContainer: {
        // Styles for the overall music section if needed
    },
    musicItem: {
        marginBottom: '25px',
        textAlign: 'left', // Align music info left
    },
    musicHeader: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#d0d0ff', // Lighter purple for headers
        marginBottom: '10px',
    },
    musicDetails: {
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        backgroundColor: '#2a2a4e', // Darker background for music item
        padding: '10px',
        borderRadius: '8px',
    },
    thumbnail: {
        width: '90px', // Smaller thumbnail
        height: '67px', // Maintain 4:3 aspect ratio
        borderRadius: '6px',
        objectFit: 'cover', // Ensure image covers the area
        flexShrink: 0, // Prevent thumbnail from shrinking
    },
    musicText: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: '0', // Allow this flex item to shrink
        flexGrow: 1, // Allow text area to take remaining space
    },
    musicTitle: {
        fontSize: '15px',
        fontWeight: '500',
        color: '#ffffff',
        margin: '0 0 4px 0', // Adjust spacing
        overflowWrap: 'break-word', // Allow long titles to wrap
        wordWrap: 'break-word', // Older fallback
        textAlign: 'left', // Keep text left aligned
    },
    musicAuthor: {
        fontSize: '13px',
        color: '#a0a0b0', // Lighter gray for author
        margin: 0,
        overflowWrap: 'break-word', // Allow long authors to wrap
        wordWrap: 'break-word', // Older fallback
        textAlign: 'left', // Keep text left aligned
    },
    noMusicText: {
        fontSize: '16px',
        color: '#a0a0b0',
        marginTop: '20px',
    },
    fullListButton: {
        marginTop: '20px',
        backgroundColor: '#6a0dad', // Darker Purple
        color: '#fff',
        padding: '12px',
        borderRadius: '8px',
        border: 'none',
        width: '100%',
        fontWeight: 'bold',
        fontSize: '16px',
        cursor: 'pointer',
        transition: 'background-color 0.3s ease',
        boxSizing: 'border-box', // Add just in case
        // ':hover': { backgroundColor: '#5a0ca0' } // Requires CSS Modules/Styled Components
    },
    selectBarButton: {
        backgroundColor: '#8A2BE2', // Purple
        color: 'white',
        padding: '15px 25px',
        borderRadius: '10px',
        fontSize: '18px',
        border: 'none',
        cursor: 'pointer',
        fontWeight: 'bold',
        transition: 'background-color 0.3s ease',
        marginTop: '30px',
        boxShadow: '0 4px 10px rgba(138, 43, 226, 0.4)', // Subtle purple glow
        // ':hover': { backgroundColor: '#7a1cc2' } // Requires CSS Modules/Styled Components
    },

    // --- Modal Styles ---
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.7)', // Darker overlay
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100, // Ensure modal is above footer and navbar
        padding: '15px', // Add padding for small screens
        boxSizing: 'border-box',
    },
    modalContent: {
        backgroundColor: '#1a1a2e', // Dark modal background
        padding: '25px', // Adjusted padding
        borderRadius: '12px',
        color: '#f0f0f0',
        width: '100%', // Take full width within overlay padding
        maxWidth: '400px', // Limit modal width on larger screens
        textAlign: 'center',
        boxShadow: '0 5px 20px rgba(0, 0, 0, 0.5)',
        maxHeight: '90vh', // Prevent modal from exceeding viewport height
        overflowY: 'auto', // Allow vertical scrolling if content overflows
        boxSizing: 'border-box', // Add box-sizing
    },
    modalTitle: {
        fontSize: '22px',
        fontWeight: 'bold',
        color: '#d0d0ff', // Light purple title
        marginBottom: '25px',
    },
    // Function to handle dynamic opacity for payment buttons
    paymentOptionButton: (isLoading) => ({
        marginBottom: '15px',
        padding: '15px',
        width: '100%',
        border: 'none',
        borderRadius: '8px',
        backgroundColor: '#6a0dad', // Purple button
        color: 'white',
        fontSize: '16px',
        fontWeight: '500',
        cursor: isLoading ? 'not-allowed' : 'pointer',
        opacity: isLoading ? 0.6 : 1,
        transition: 'background-color 0.3s, opacity 0.3s',
        // ':hover': { backgroundColor: isLoading ? '#6a0dad' : '#5a0ca0' } // Requires CSS Modules/Styled Components
    }),
    paymentDetailsContainer: {
        marginTop: '25px',
        paddingTop: '20px',
        borderTop: '1px solid #3a3a5e', // Separator
    },
    paymentInstructions: {
        marginBottom: '15px',
        fontSize: '15px',
        color: '#c0c0c0', // Lighter gray
    },
    qrCodeImage: {
        width: '180px', // Slightly smaller QR code
        height: '180px',
        display: 'block',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginBottom: '15px',
        backgroundColor: 'white', // Ensure QR code background is white for scanning
        padding: '10px', // Padding around QR code
        borderRadius: '8px',
    },
    copyPixButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        marginTop: '10px',
        padding: '12px',
        width: '100%',
        backgroundColor: '#2a2a4e', // Darker button
        color: '#d0d0ff', // Light purple text/icon
        border: '1px solid #4a4a6e',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '15px',
        transition: 'background-color 0.2s',
        // ':hover': { backgroundColor: '#3a3a5e' } // Requires CSS Modules/Styled Components
    },
    pixCodePreview: {
        fontSize: '12px',
        color: '#888',
        marginTop: '10px',
        wordBreak: 'break-all', // Prevent long codes from overflowing (already present)
        fontFamily: 'monospace', // Use monospace for code
        backgroundColor: '#12121f',
        padding: '5px 8px', // Adjusted padding
        borderRadius: '4px',
        textAlign: 'left', // Align code left
        overflowWrap: 'break-word', // Ensure wrapping (redundant with word-break but safe)
        wordWrap: 'break-word',
    },
    modalCloseButton: {
        backgroundColor: '#E60000',
        color: 'white',
        padding: '12px',
        width: '100%',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        marginTop: '25px', // Space above close button
        fontSize: '16px',
        fontWeight: 'bold',
        transition: 'background-color 0.2s',
        // ':hover': { backgroundColor: '#c00000' } // Requires CSS Modules/Styled Components
    },
}; // --- End of Styles Object ---
